import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  ResponsiveContainer
} from 'recharts';
import { TrendingUp, BarChart3, PieChart as PieChartIcon, ClipboardList, Wallet } from 'lucide-react';

/**
 * La palette des tracés, aux couleurs de Kokpit.
 *
 * L'ancienne était celle de Material — vert, bleu, orange, violet, rouge —, sans rapport avec le
 * produit. Un graphique posé au milieu d'une conversation en noir et terre de Sienne y détonnait
 * plus qu'il n'informait.
 *
 * **Une rampe, et non un arc-en-ciel.** Les séries d'un graphique de performance sont presque
 * toujours ordonnées — du plus dépensier au moins, du meilleur au pire. Des teintes qui dégradent
 * depuis l'accent portent cet ordre ; cinq couleurs franches le contredisent en suggérant des
 * catégories sans rapport entre elles.
 *
 * Les quatre dernières sont des gris : au-delà de six séries, la distinction par la teinte ne
 * fonctionne plus de toute façon, et mieux vaut un fond neutre qu'une couleur de plus.
 */
const CHART_COLORS = [
  '#7c2d12', // terre de Sienne — l'accent de la charte
  '#b45309', // ambre foncé
  '#1a1a1a', // noir des titres
  '#a16207', // ocre
  '#374151', // gris foncé
  '#c2410c', // terre de Sienne claire
  '#6b7280', // gris secondaire
  '#9ca3af',
  '#d1d5db',
  '#e5e7eb'
];

/** La couleur d'une série unique : l'accent, toujours. */
const COULEUR_PRINCIPALE = CHART_COLORS[0];

/*
 * Le décor des graphiques : grille, axes, infobulle, légende.
 *
 * Recharts peint par défaut en noir plein, avec une infobulle bordée de gris bleuté. Posé dans
 * une conversation dont tout le reste est en #e5e7eb et #6b7280, cela tire l'œil vers le cadre
 * plutôt que vers les barres. Les graduations passent donc en gris secondaire et les filets au
 * gris des séparateurs — le même que les bordures de tableau juste au-dessus.
 */
const AXE = { fill: '#6b7280', fontSize: 12 };

const INFOBULLE = {
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '0.85rem',
  color: '#1a1a1a'
};

const LEGENDE = { fontSize: '0.8rem', color: '#6b7280' };

/*
 * ─── Mise en forme des nombres ─────────────────────────────────────────────────────────────
 *
 * Les graphiques affichaient `280000` et `1002`. Au-delà de quatre chiffres, un nombre sans
 * séparateur se lit en comptant les zéros : c'est l'ordre de grandeur, la seule chose qu'un
 * graphique est censé donner d'un coup d'œil, qui demande un effort.
 *
 * Et une somme d'argent sans son symbole se confond avec un volume. « 280 000 » sur un axe peut
 * être du chiffre d'affaires comme des sessions ; l'axe ne porte pas de titre.
 */

/** Les mots qui, pris entiers, désignent une somme d'argent. */
const JETONS_MONETAIRES = new Set([
  'cost', 'costs', 'cout', 'couts', 'spend', 'spends', 'depense', 'depenses', 'budget',
  'revenue', 'revenues', 'revenu', 'revenus', 'ca', 'montant', 'montants', 'prix',
  'cpc', 'cpa', 'cpm', 'cpl', 'aov', 'panier', 'commission', 'commissions',
  'valeur', 'value', 'gain', 'gains', 'marge'
]);

/** Les mots qui désignent un pourcentage. */
const JETONS_POURCENT = new Set([
  'ctr', 'taux', 'rate', 'cos', 'part', 'share', 'pourcentage', 'pct'
]);

/**
 * Les mots qui ressemblent à de l'argent sans en être.
 *
 * `roas` est un rapport — 4,2, pas 4,20 € — et `conversionsValue` porte bien une valeur
 * monétaire, mais `conversions` seul est un décompte. Sans cette liste, `valeur` suffirait à
 * monnayer n'importe quoi.
 */
const JETONS_NEUTRES = new Set(['roas', 'ratio', 'index', 'score', 'nombre', 'count']);

/**
 * Découpe un nom de champ en mots : `valeurConversions` → `valeur`, `conversions`.
 *
 * Les noms arrivent dans tous les styles — camelCase de GA4, snake_case des régies, français du
 * regroupement maison. Un test par sous-chaîne confondrait `ca` avec « catégorie » ou
 * « campagne » ; on compare donc des mots entiers.
 */
const motsDuChamp = (nom) => String(nom || '')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .split(/[^a-z0-9]+/)
  .filter(Boolean);

/**
 * L'unité d'une série : `'€'`, `'%'` ou rien.
 *
 * Déduite du nom du champ, faute de mieux : le modèle n'annonce pas ses unités, et lui demander
 * de le faire ajouterait une propriété de plus à se tromper. Il peut toutefois trancher avec
 * `unite` quand la déduction se révèle fausse — un champ nommé « total » par exemple.
 */
export const uniteDuChamp = (nom) => {
  const mots = motsDuChamp(nom);
  if (mots.some((m) => JETONS_NEUTRES.has(m))) return null;
  if (mots.some((m) => JETONS_POURCENT.has(m))) return '%';
  if (mots.some((m) => JETONS_MONETAIRES.has(m))) return '€';
  // Les noms composés que le découpage ne sépare pas : `itemRevenue` est déjà couvert, pas
  // `chiffredaffaires` écrit d'un bloc.
  const colle = mots.join('');
  if (/chiffredaffaire|turnover/.test(colle)) return '€';
  return null;
};

/** L'unité commune à plusieurs séries, ou rien si elles divergent. */
const uniteDesSeries = (champs, unite) => {
  if (unite !== undefined) return unite || null;
  const unites = new Set(champs.map(uniteDuChamp));
  return unites.size === 1 ? [...unites][0] : null;
};

const formatFr = (min, max) => new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: min, maximumFractionDigits: max
});

const ENTIER = formatFr(0, 0);
const DECIMAL = formatFr(0, 2);
/*
 * Une somme à centimes s'écrit avec ses deux décimales : « 1 002,5 € » n'est pas une façon
 * d'écrire de l'argent. Un montant rond, lui, n'a pas de centimes à montrer.
 */
const MONNAIE = formatFr(2, 2);
const UNE_DECIMALE = formatFr(0, 1);

/**
 * Un nombre tel qu'on l'écrit en français, avec son unité.
 *
 * `fr-FR` sépare par une espace insécable étroite : la coupure de ligne ne peut pas tomber au
 * milieu du nombre.
 *
 * `entier` est le mode des graduations d'axe, où la place manque et où la décimale n'apprend
 * rien — sauf sur un axe de pourcentages aux valeurs basses, un CTR entre 0,4 et 2 % n'ayant que
 * des graduations à 0, 1 et 2 si on l'arrondit.
 */
export const formaterValeur = (valeur, unite, { entier = false, decimale = false } = {}) => {
  if (typeof valeur !== 'number' || !Number.isFinite(valeur)) return valeur;

  let format;
  if (entier) format = decimale ? UNE_DECIMALE : ENTIER;
  else if (unite === '€') format = Number.isInteger(valeur) ? ENTIER : MONNAIE;
  else format = DECIMAL;

  const texte = format.format(valeur);
  if (unite === '€') return `${texte} €`;
  if (unite === '%') return `${texte} %`;
  return texte;
};

/**
 * Le centre d'un `viewBox` Recharts, quelle que soit sa forme.
 *
 * **Deux formes existent, et on ne choisit pas laquelle arrive.** `ViewBox` vaut soit un repère
 * polaire (`cx`, `cy`), soit un repère cartésien (`x`, `y`, `width`, `height`) — c'est écrit dans
 * les types de la bibliothèque. En ne lisant que `cx`/`cy`, on obtenait `undefined`, et le texte
 * se posait en haut à gauche du cadre, les deux lignes l'une sur l'autre.
 *
 * Le repli cartésien n'est pas une approximation : le camembert est centré sur `50%` de cette
 * même zone de tracé, légende exclue. Son milieu est donc exactement le milieu du rectangle.
 *
 * Rend `null` quand aucune des deux formes n'est exploitable — mieux vaut pas d'étiquette qu'une
 * étiquette dans un coin.
 */
const centreDuViewBox = (viewBox) => {
  if (!viewBox) return null;
  if (typeof viewBox.cx === 'number' && typeof viewBox.cy === 'number') {
    return { x: viewBox.cx, y: viewBox.cy };
  }
  if (typeof viewBox.x === 'number' && typeof viewBox.width === 'number'
      && typeof viewBox.y === 'number' && typeof viewBox.height === 'number') {
    return { x: viewBox.x + viewBox.width / 2, y: viewBox.y + viewBox.height / 2 };
  }
  return null;
};

/** Un axe de pourcentages aux valeurs basses garde une décimale, faute de quoi il n'a plus de graduations. */
const axeAvecDecimale = (data, champs, unite) => {
  if (unite !== '%') return false;
  const valeurs = (data || []).flatMap((l) => champs.map((c) => l[c]))
    .filter((v) => typeof v === 'number' && Number.isFinite(v));
  return valeurs.length > 0 && Math.max(...valeurs.map(Math.abs)) < 10;
};

/**
 * Les graduations de l'axe, et la place qu'elles réclament.
 *
 * Recharts réserve 60 px à l'axe des ordonnées. « 280 000 € » en demande une vingtaine de plus,
 * et ce qui dépasse est coupé — on lirait « 80 000 € ». La largeur se calcule donc sur la plus
 * longue graduation réellement rendue, et non sur une valeur en dur.
 */
const largeurAxe = (data, champs, unite) => {
  const valeurs = (data || []).flatMap((ligne) => champs.map((c) => ligne[c]))
    .filter((v) => typeof v === 'number' && Number.isFinite(v));
  if (valeurs.length === 0) return 60;
  const extremes = [Math.min(...valeurs), Math.max(...valeurs)];
  const plusLong = Math.max(...extremes.map((v) => formaterValeur(v, unite, { entier: true }).length));
  return Math.max(60, Math.round(plusLong * 7.5) + 12);
};

/**
 * Les séries à tracer sur un graphique : une ou plusieurs.
 *
 * **Pourquoi plusieurs.** Comparer deux catégories mois par mois est la question la plus
 * naturelle qu'on pose à ces graphiques, et le format n'acceptait qu'un `yField` : l'analyse
 * traçait une courbe, décrivait la seconde en toutes lettres, et concluait qu'« il faudrait un
 * rendu à deux lignes ». Le lecteur voyait alors une courbe et lisait une comparaison.
 *
 * `yFields: ["1b", "1g"]` trace les deux sur le même repère. `yField` reste accepté — c'est la
 * forme courante, et le modèle l'écrit de lui-même pour une série unique.
 *
 * L'ordre de `CHART_COLORS` fait le reste : la première série porte l'accent de la charte, les
 * suivantes s'en éloignent par paliers.
 */
export const seriesDemandees = ({ yFields, yField, color }) => {
  const champs = Array.isArray(yFields) && yFields.length > 0
    ? yFields.filter((c) => typeof c === 'string' && c)
    : (yField ? [yField] : []);

  return champs.map((champ, index) => ({
    champ,
    // `color` ne vaut que pour une série unique : sur plusieurs, il les peindrait toutes pareil.
    couleur: champs.length === 1 && color ? color : CHART_COLORS[index % CHART_COLORS.length]
  }));
};

// Composant Graphique Linéaire
export const LineChartArtifact = ({ data, title, xField, yField, yFields, color, unite }) => {
  const series = seriesDemandees({ yFields, yField, color: color || COULEUR_PRINCIPALE });
  const champs = series.map((s) => s.champ);
  const uniteRetenue = uniteDesSeries(champs, unite);
  return (
    <div className="artifact-container">
      <div className="artifact-header">
        <h3 className="artifact-title"><TrendingUp size={18} />&nbsp;{title}</h3>
      </div>
      <div className="artifact-chart">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={xField} tick={AXE} stroke="#e5e7eb" />
            <YAxis
              tick={AXE}
              stroke="#e5e7eb"
              width={largeurAxe(data, champs, uniteRetenue)}
              tickFormatter={(v) => formaterValeur(v, uniteRetenue, {
                entier: true,
                decimale: axeAvecDecimale(data, champs, uniteRetenue)
              })}
            />
            <Tooltip
              contentStyle={INFOBULLE}
              /* L'infobulle garde les décimales : c'est là qu'on vient chercher la valeur exacte. */
              formatter={(v) => formaterValeur(v, uniteRetenue)}
            />
            <Legend wrapperStyle={LEGENDE} />
            {series.map(({ champ, couleur }) => (
              <Line
                key={champ}
                type="monotone"
                dataKey={champ}
                stroke={couleur}
                strokeWidth={3}
                /* Au-delà de deux séries, les pastilles se chevauchent plus qu'elles n'aident. */
                dot={series.length > 2 ? false : { fill: couleur, strokeWidth: 2, r: 6 }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/**
 * Graphique en barres.
 *
 * **`isAnimationActive={false}` n'est pas cosmétique.** Avec Recharts 3.8, l'animation d'entrée
 * laissait la barre à l'état initial : le groupe `recharts-bar-rectangle` était bien créé, mais
 * vide — aucun tracé à l'intérieur. Constaté côte à côte : les neuf graphiques du dashboard
 * peignaient leurs barres (7 tracés pour 7 lignes), celui du panneau zéro pour une ligne.
 *
 * Le dashboard consolidé désactive déjà l'animation à un endroit, pour une raison voisine.
 * L'animation n'apporte rien ici : le graphique apparaît au terme d'une réponse qu'on vient de
 * lire se dérouler.
 */
// Composant Graphique en Barres
export const BarChartArtifact = ({ data, title, xField, yField, yFields, color, unite }) => {
  const series = seriesDemandees({ yFields, yField, color: color || COULEUR_PRINCIPALE });
  const champs = series.map((s) => s.champ);
  const uniteRetenue = uniteDesSeries(champs, unite);
  return (
    <div className="artifact-container">
      <div className="artifact-header">
        <h3 className="artifact-title"><BarChart3 size={18} />&nbsp;{title}</h3>
      </div>
      <div className="artifact-chart">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={xField} tick={AXE} stroke="#e5e7eb" />
            <YAxis
              tick={AXE}
              stroke="#e5e7eb"
              width={largeurAxe(data, champs, uniteRetenue)}
              tickFormatter={(v) => formaterValeur(v, uniteRetenue, {
                entier: true,
                decimale: axeAvecDecimale(data, champs, uniteRetenue)
              })}
            />
            <Tooltip
              contentStyle={INFOBULLE}
              /* L'infobulle garde les décimales : c'est là qu'on vient chercher la valeur exacte. */
              formatter={(v) => formaterValeur(v, uniteRetenue)}
            />
            <Legend wrapperStyle={LEGENDE} />
            {/* Animation d'entrée désactivée : voir le commentaire de `BarChartArtifact`. */}
            {series.map(({ champ, couleur }) => (
              <Bar key={champ} dataKey={champ} fill={couleur} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Composant Graphique en Aires
export const AreaChartArtifact = ({ data, title, xField, yField, yFields, color, unite }) => {
  const series = seriesDemandees({ yFields, yField, color: color || COULEUR_PRINCIPALE });
  const champs = series.map((s) => s.champ);
  const uniteRetenue = uniteDesSeries(champs, unite);
  return (
    <div className="artifact-container">
      <div className="artifact-header">
        <h3 className="artifact-title"><TrendingUp size={18} />&nbsp;{title}</h3>
      </div>
      <div className="artifact-chart">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={xField} tick={AXE} stroke="#e5e7eb" />
            <YAxis
              tick={AXE}
              stroke="#e5e7eb"
              width={largeurAxe(data, champs, uniteRetenue)}
              tickFormatter={(v) => formaterValeur(v, uniteRetenue, {
                entier: true,
                decimale: axeAvecDecimale(data, champs, uniteRetenue)
              })}
            />
            <Tooltip
              contentStyle={INFOBULLE}
              /* L'infobulle garde les décimales : c'est là qu'on vient chercher la valeur exacte. */
              formatter={(v) => formaterValeur(v, uniteRetenue)}
            />
            <Legend wrapperStyle={LEGENDE} />
            {series.map(({ champ, couleur }) => (
              <Area
                key={champ}
                isAnimationActive={false}
                type="monotone"
                dataKey={champ}
                stroke={couleur}
                fill={couleur}
                /* Des aires empilées s'occultent : plus elles sont nombreuses, plus on éclaircit. */
                fillOpacity={series.length > 1 ? 0.2 : 0.3}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Composant Graphique Circulaire
/**
 * Camembert, ou donut quand on le troue.
 *
 * ## Deux défauts de légende, une seule cause
 *
 * La légende affichait **six fois « visits »**, et les étiquettes de parts « 0 55 % », « 1 26 % »,
 * « 2 12 % » — l'indice de la part au lieu de son nom. Recharts nomme ses parts d'après
 * `nameKey`, dont la valeur par défaut est `"name"` ; nos lignes portent `referrer`, `country`,
 * `famille`… Faute de ce champ, il retombait sur `dataKey` pour la légende et sur l'indice pour
 * l'étiquette. Un camembert dont aucune part n'est nommée ne dit rien du tout.
 *
 * ## Les petites parts n'écrivent plus par-dessus leurs voisines
 *
 * Sur la capture, cinq parts sous 5 % empilaient leurs étiquettes au même endroit — « 5 1 % »
 * posé sur « 4 1 % » posé sur « 3 4 % ». En deçà du seuil, le nom reste dans la légende et au
 * survol, là où il se lit.
 */
export const PieChartArtifact = ({
  data, title, nameField, valueField, unite,
  /** Troue le centre. Un `innerRadius` explicite l'emporte, pour qui veut doser l'anneau. */
  donut = false, innerRadius
}) => {
  const uniteRetenue = uniteDesSeries([valueField], unite);
  const rayonInterne = innerRadius ?? (donut ? 50 : 0);

  const total = (data || []).reduce((somme, l) => somme + (Number(l[valueField]) || 0), 0);

  return (
    <div className="artifact-container">
      <div className="artifact-header">
        <h3 className="artifact-title"><PieChartIcon size={18} />&nbsp;{title}</h3>
      </div>
      <div className="artifact-chart">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              isAnimationActive={false}
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              /* Sans `nameKey`, Recharts cherche un champ `name` et retombe sur l'indice. */
              nameKey={nameField}
              label={({ name, percent }) => (percent >= 0.05
                ? `${name} ${(percent * 100).toFixed(0)} %`
                : null)}
              innerRadius={rayonInterne}
              outerRadius={80}
              dataKey={valueField}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
              {/*
                Le trou d'un donut est une place offerte : le total y répond à la question que
                pose toute répartition — « sur combien ? ».

                **Le total doit se placer dans le repère du camembert, pas dans celui du SVG.**
                Écrit en `x="50%" y="50%"`, il visait le milieu de l'image ; or Recharts réserve
                le bas du cadre à la légende, et le disque est donc plus haut et décalé. Le
                chiffre tombait à côté du trou. `Label` reçoit le `viewBox` du camembert —
                `cx`, `cy` réels — et s'y pose exactement.
              */}
              {rayonInterne > 0 && total > 0 && (
                <Label
                  position="center"
                  content={({ viewBox }) => {
                    const centre = centreDuViewBox(viewBox);
                    if (!centre) return null;
                    return (
                    <>
                      <text
                        x={centre.x}
                        y={centre.y - 7}
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{ fill: '#1a1a1a', fontSize: '1rem', fontWeight: 600 }}
                      >
                        {formaterValeur(total, uniteRetenue, { entier: true })}
                      </text>
                      {/*
                        **« total » tout court serait un abus.** Une répartition est souvent
                        tronquée — « top 10 des pays » — et la somme des parts tracées n'est
                        alors pas celle du compte. On dit donc ce qu'on additionne : ce qui est
                        affiché, et rien d'autre.
                      */}
                      <text
                        x={centre.x}
                        y={centre.y + 9}
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{ fill: '#6b7280', fontSize: '0.7rem' }}
                      >
                        cumul affiché
                      </text>
                    </>
                    );
                  }}
                />
              )}
            </Pie>
            <Tooltip contentStyle={INFOBULLE} formatter={(v) => formaterValeur(v, uniteRetenue)} />
            <Legend wrapperStyle={LEGENDE} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Composant Tableau Interactif
export const InteractiveTable = ({ data, title, columns }) => {
  const [sortConfig, setSortConfig] = React.useState({ key: null, direction: 'asc' });

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↗️' : '↘️';
  };

  return (
    <div className="artifact-container">
      <div className="artifact-header">
        <h3 className="artifact-title"><ClipboardList size={18} />&nbsp;{title}</h3>
      </div>
      <div className="artifact-table-wrapper">
        <table className="artifact-table">
          <thead>
            <tr>
              {columns.map(column => (
                <th 
                  key={column.key} 
                  onClick={() => handleSort(column.key)}
                  className="sortable"
                >
                  {column.label} {getSortIcon(column.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, index) => (
              <tr key={index}>
                {columns.map(column => (
                  <td key={column.key} className={column.type === 'number' ? 'numeric' : ''}>
                    {/*
                      * Un tableau posé sous un graphique doit écrire ses nombres comme lui : les
                      * deux portent les mêmes chiffres, et « 280 000 € » au-dessus de « 280000 »
                      * se lit comme deux grandeurs différentes.
                      */}
                    {column.format
                      ? column.format(row[column.key])
                      : formaterValeur(row[column.key], uniteDuChamp(column.key))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Composant KPI Cards
export const KPICards = ({ kpis, title }) => {
  return (
    <div className="artifact-container">
      <div className="artifact-header">
        <h3 className="artifact-title"><Wallet size={18} />&nbsp;{title}</h3>
      </div>
      <div className="kpi-grid">
        {kpis.map((kpi, index) => (
          <div key={index} className={`kpi-card ${kpi.trend || 'neutral'}`}>
            <div className="kpi-icon">{kpi.icon}</div>
            <div className="kpi-content">
              <div className="kpi-label">{kpi.label}</div>
              <div className="kpi-value">{kpi.value}</div>
              {kpi.change && (
                <div className={`kpi-change ${kpi.trend}`}>
                  {kpi.trend === 'positive' ? '↗️' : kpi.trend === 'negative' ? '↘️' : '➡️'} {kpi.change}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Composant Multi-Chart (plusieurs graphiques)
export const MultiChart = ({ data, title, charts }) => {
  return (
    <div className="artifact-container">
      <div className="artifact-header">
        <h3 className="artifact-title"><BarChart3 size={18} />&nbsp;{title}</h3>
      </div>
      <div className="multi-chart-grid">
        {charts.map((chart, index) => (
          <div key={index} className="chart-item">
            <h4>{chart.title}</h4>
            <ResponsiveContainer width="100%" height={200}>
              {chart.type === 'line' && (
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey={chart.xField} />
                  <YAxis
                    tick={AXE}
                    stroke="#e5e7eb"
                    width={largeurAxe(data, [chart.yField], uniteDuChamp(chart.yField))}
                    tickFormatter={(v) => formaterValeur(v, uniteDuChamp(chart.yField), { entier: true })}
                  />
                  <Tooltip contentStyle={INFOBULLE} formatter={(v) => formaterValeur(v, uniteDuChamp(chart.yField))} />
                  <Line 
                    type="monotone" 
                    dataKey={chart.yField} 
                    stroke={CHART_COLORS[index % CHART_COLORS.length]} 
                  />
                </LineChart>
              )}
              {chart.type === 'bar' && (
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey={chart.xField} />
                  <YAxis
                    tick={AXE}
                    stroke="#e5e7eb"
                    width={largeurAxe(data, [chart.yField], uniteDuChamp(chart.yField))}
                    tickFormatter={(v) => formaterValeur(v, uniteDuChamp(chart.yField), { entier: true })}
                  />
                  <Tooltip contentStyle={INFOBULLE} formatter={(v) => formaterValeur(v, uniteDuChamp(chart.yField))} />
                  <Bar dataKey={chart.yField} fill={CHART_COLORS[index % CHART_COLORS.length]} isAnimationActive={false} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </div>
  );
};

// Fonction de rendu principal des artefacts
/*
 * ─── Retrouver les champs d'un graphique ───────────────────────────────────────────────────
 *
 * **Le problème.** Les propriétés sont écrites par un modèle d'après un format décrit en prose.
 * Il écrit le plus souvent `xField`/`yField`, mais pas toujours : `xKey`, `x`, `categoryField`
 * passent aussi, et il lui arrive de n'en écrire aucun en laissant les données parler
 * d'elles-mêmes. Observé : « Graphique non affiché : le champ d'abscisse « (absent) » n'existe
 * pas dans les données », devant un jeu de dix mois parfaitement traçable.
 *
 * Refuser pour cela revient à jeter une donnée valide à cause de l'étiquette de son enveloppe —
 * la même raison qui fait déjà accepter les propriétés à plat comme imbriquées.
 *
 * **Ce qu'on ne fait pas :** deviner à la place d'une valeur fournie. Un `xField` écrit, même
 * inexact, reste signalé — c'est une faute à corriger, pas une omission à combler.
 */

/** Les noms sous lesquels un modèle écrit l'abscisse. */
const ALIAS_X = ['xField', 'xKey', 'x', 'xAxis', 'categoryField', 'labelField', 'nameField'];

/** Et l'ordonnée, au singulier puis au pluriel. */
const ALIAS_Y = ['yField', 'yKey', 'y', 'yAxis', 'valueField', 'metricField'];
const ALIAS_Y_PLURIEL = ['yFields', 'yKeys', 'series', 'seriesFields', 'metrics'];

const premierDefini = (props, cles) => cles.map((c) => props[c]).find((v) => v !== undefined);

/**
 * Les propriétés d'un graphique, avec ses champs retrouvés quand ils manquent.
 *
 * L'abscisse se déduit du premier champ non numérique de la première ligne — un mois, un pays,
 * un nom de produit. Les séries, de tous les champs numériques restants : sur `{mois, ChatGPT,
 * Gemini}`, c'est exactement ce qu'on veut tracer.
 */
export const retrouverChamps = (type, props = {}) => {
  const estGraphe = ['line_chart', 'bar_chart', 'area_chart'].includes(type);
  const estUnCamembert = type === 'pie_chart' || type === 'donut_chart';
  if (!estGraphe && !estUnCamembert) return props;

  const premiere = Array.isArray(props.data) && props.data.length > 0 ? props.data[0] : null;
  if (!premiere) return props;

  const cles = Object.keys(premiere);
  const nonNumeriques = cles.filter((c) => typeof premiere[c] !== 'number');
  const numeriques = cles.filter((c) => typeof premiere[c] === 'number');

  if (estUnCamembert) {
    return {
      ...props,
      nameField: props.nameField ?? premierDefini(props, ALIAS_X) ?? nonNumeriques[0],
      valueField: props.valueField ?? premierDefini(props, ALIAS_Y) ?? numeriques[0]
    };
  }

  const x = props.xField ?? premierDefini(props, ALIAS_X) ?? nonNumeriques[0] ?? cles[0];
  const yPluriel = props.yFields ?? premierDefini(props, ALIAS_Y_PLURIEL);
  const ySingulier = props.yField ?? premierDefini(props, ALIAS_Y);

  const sortie = { ...props, xField: x };
  if (Array.isArray(yPluriel) && yPluriel.length > 0) sortie.yFields = yPluriel;
  else if (ySingulier !== undefined) sortie.yField = ySingulier;
  // Aucune série nommée : toutes les colonnes numériques en sont une.
  else sortie.yFields = numeriques.filter((c) => c !== x);

  return sortie;
};

/**
 * Un artefact est-il traçable ?
 *
 * **Pourquoi ce garde-fou.** Les propriétés d'un graphique sont écrites par un modèle, à partir
 * d'un format décrit dans un prompt. Un nom de champ inexact — `cost` au lieu de `cout`, ou une
 * valeur rendue en `"8 458 €"` plutôt qu'en nombre — ne lève rien : Recharts trace des axes et
 * aucune barre. On obtient un cadre vide qui se lit comme « il n'y a rien à montrer », alors que
 * la donnée est là et que c'est la description qui est fausse.
 *
 * Mieux vaut dire laquelle.
 */
const verifierArtefact = (type, props = {}) => {
  const lignes = props.data;
  const premiere = Array.isArray(lignes) && lignes.length > 0 ? lignes[0] : null;

  const estUnGraphe = ['line_chart', 'bar_chart', 'area_chart'].includes(type);

  const estUnCamembert = type === 'pie_chart' || type === 'donut_chart';

  if (estUnGraphe || estUnCamembert || type === 'table' || type === 'interactive_table') {
    if (!premiere) return 'aucune donnée à tracer';
  }

  /*
   * Les graphes à séries se vérifient série par série.
   *
   * Nommer « le champ X » quand c'est Y qui manque envoie corriger le mauvais endroit : avec
   * `yFields`, une seule courbe sur trois peut porter un nom inexact, et c'est celle-là qu'il
   * faut citer.
   */
  if (estUnGraphe) {
    if (!props.xField || premiere[props.xField] === undefined) {
      return `le champ d'abscisse « ${props.xField || '(absent)'} » n'existe pas dans les données`;
    }

    const series = seriesDemandees(props);
    if (series.length === 0) return 'aucune série à tracer (ni `yField` ni `yFields`)';

    const inconnues = series.filter(({ champ }) => premiere[champ] === undefined);
    if (inconnues.length > 0) {
      return `${inconnues.length > 1 ? 'les champs' : 'le champ'} « ${inconnues.map((s) => s.champ).join(' », « ')} » n'existe${inconnues.length > 1 ? 'nt' : ''} pas dans les données`;
    }

    const nonNumeriques = series.filter(({ champ }) => typeof premiere[champ] !== 'number');
    if (nonNumeriques.length > 0) {
      const { champ } = nonNumeriques[0];
      return `les valeurs de « ${champ} » ne sont pas numériques (${JSON.stringify(premiere[champ])})`;
    }
  }

  if (estUnCamembert) {
    const manquants = ['nameField', 'valueField']
      .filter((cle) => !props[cle] || premiere[props[cle]] === undefined);
    if (manquants.length > 0) {
      return `le champ « ${manquants.map((c) => props[c] || '(absent)').join(' », « ')} » n'existe pas dans les données`;
    }
    if (typeof premiere[props.valueField] !== 'number') {
      return `les valeurs ne sont pas numériques (${JSON.stringify(premiere[props.valueField])})`;
    }
  }

  if (type === 'kpi_cards' && !(Array.isArray(props.kpis) && props.kpis.length > 0)) {
    return 'aucun indicateur à afficher';
  }

  return null;
};

export const ArtifactRenderer = ({ artifact }) => {
  if (!artifact || !artifact.type) {
    return null;
  }

  /*
   * Le type, ramené à une forme canonique.
   *
   * Au-delà du préfixe `create_`, les noms varient : « doughnut » est l'orthographe britannique
   * de « donut », et le suffixe `_chart` saute volontiers. Trois façons d'écrire la même
   * intention, dont deux rendraient « Type d'artefact non supporté » devant des données valides.
   */
  const TYPES_SYNONYMES = {
    doughnut_chart: 'donut_chart',
    doughnut: 'donut_chart',
    donut: 'donut_chart',
    pie: 'pie_chart',
    line: 'line_chart',
    bar: 'bar_chart',
    area: 'area_chart',
    column_chart: 'bar_chart'
  };
  const typeBrut = artifact.type.replace('create_', '').toLowerCase();
  const normalizedType = TYPES_SYNONYMES[typeBrut] || typeBrut;

  /*
   * Les propriétés sont acceptées **imbriquées ou à plat**.
   *
   * Le format demandé au modèle les place sous `props`, mais il les écrit couramment au premier
   * niveau : `{type, title, data, xField, yField}`. Les données sont alors parfaitement valides
   * et seule l'enveloppe diffère — refuser pour cela afficherait « aucune donnée à tracer »
   * devant un jeu de données complet, ce qui est le contraire de ce qu'on cherche à dire.
   *
   * `props` reste prioritaire quand les deux existent.
   */
  const brutes = artifact.props && typeof artifact.props === 'object' ? artifact.props : artifact;
  const props = retrouverChamps(normalizedType, brutes);

  const motif = verifierArtefact(normalizedType, props);
  if (motif) {
    return (
      <div className="artifact-error">
        Graphique non affiché : {motif}.
      </div>
    );
  }

  switch (normalizedType) {
    case 'line_chart':
      return <LineChartArtifact {...props} />;
    case 'bar_chart':
      return <BarChartArtifact {...props} />;
    case 'area_chart':
      return <AreaChartArtifact {...props} />;
    case 'pie_chart':
      return <PieChartArtifact {...props} />;
    // Un donut est un camembert troué : même validation, même rendu, un rayon interne en plus.
    case 'donut_chart':
      return <PieChartArtifact donut {...props} />;
    case 'interactive_table':
    case 'table':
      return <InteractiveTable {...props} />;
    case 'kpi_cards':
      return <KPICards {...props} />;
    case 'multi_chart':
      return <MultiChart {...props} />;
    default:
      return <div className="artifact-error">Type d'artefact non supporté: {artifact.type}</div>;
  }
};

export default ArtifactRenderer;